import React from 'react';
import { Box, Divider, Grid } from '@mui/material';

import { ContainerColumnLeft } from '@occc-app/design-system';
import { Typography, Card, CardContent } from '@occc/ui-design-system';
import { CommunicationPreferenceFragment, CommunicationPreferenceUpdateInput } from '@occc/graphql';
import { useConfig } from '@occc/config';
import { getCommunicationPreferencesMap, getSubBrandName } from './communication-preferences.utils';
import { CommunicationPreferenceItem } from './communication-preference-item.component';

type CommunicationPreferenceProps = {
  communicationPreferences: CommunicationPreferenceFragment[];
  updateCommunicationPreference: (
    input: Omit<CommunicationPreferenceUpdateInput, 'customerUuid'>,
  ) => void;
  isUSPhoneNumber: boolean;
  updateInProgress: boolean;
  isUserPermittedToEdit: boolean;
};

export const CommunicationPreference = ({
  communicationPreferences,
  updateCommunicationPreference,
  updateInProgress,
  isUSPhoneNumber,
  isUserPermittedToEdit,
}: CommunicationPreferenceProps) => {
  const { config } = useConfig();

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    name: string,
    subBrandUuid?: string | null,
  ) => {
    const key = e.target.checked ? `${name}InAt` : `${name}OutAt`;

    updateCommunicationPreference({
      [key]: new Date().toISOString(),
      subBrandUuid: subBrandUuid ?? undefined,
    });
  };

  return (
    <ContainerColumnLeft>
      <Card sx={{ mb: 0 }}>
        <CardContent>
          <Typography variant='h6' component='h3' paragraph>
            Communication Preferences
          </Typography>
          {communicationPreferences.map((communicationPreference, index) => {
            const { subBrandUuid } = communicationPreference;
            const communicationPreferenceMap = getCommunicationPreferencesMap(
              communicationPreference,
              config,
            );
            const subBrandName = getSubBrandName(config, subBrandUuid);

            return (
              <Box key={`${subBrandUuid || 'brand'}-${index}`}>
                {index > 0 && <Divider sx={{ my: 2 }} />}

                {communicationPreferences.length > 1 ? (
                  <Typography variant='body1' sx={{ mb: 2 }}>
                    {subBrandName}
                  </Typography>
                ) : null}

                <Grid container spacing={2}>
                  {communicationPreferenceMap.map((item, itemIndex) => (
                    <CommunicationPreferenceItem
                      key={`${subBrandUuid || 'brand'}-${index}-${itemIndex}-${item.name}`}
                      item={item}
                      subBrandUuid={subBrandUuid}
                      isUSPhoneNumber={isUSPhoneNumber}
                      updateInProgress={updateInProgress}
                      isUserPermittedToEdit={isUserPermittedToEdit}
                      onChange={onChange}
                    />
                  ))}
                </Grid>
              </Box>
            );
          })}
        </CardContent>
      </Card>
    </ContainerColumnLeft>
  );
};
